import { useEffect, useState } from 'react';

import { useUser } from '@feathr/extender/state';

interface IUseRequireSudoReturn {
  readonly isAuthorized: boolean;
  readonly isPending: boolean;
}

/**
 * Ensures the current user has sudo access for Perch admin tooling.
 * Automatically redirects non-sudo users to the main app root.
 *
 * @returns Object containing authorization and loading state
 * @example
 * function PerchApp(): JSX.Element {
 *   const { isAuthorized, isPending } = useRequireSudo();
 *
 *   if (isPending) return <Spinner />;
 *   if (!isAuthorized) return <></>; // redirect already triggered
 *
 *   return <PerchLayout>...</PerchLayout>;
 * }
 */
function useRequireSudo(): IUseRequireSudoReturn {
  const user = useUser({ required: false });
  const isPending = !user || user.isPending;
  const isSudoer = user ? user.isSudoer : false;
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    if (!isPending && !isSudoer && !hasRedirected) {
      setHasRedirected(true);
      /* Full page reload to clear any Perch-specific state from the React tree */
      globalThis.location.assign('/');
    }
  }, [isPending, isSudoer, hasRedirected]);

  return {
    isAuthorized: isSudoer,
    isPending,
  };
}

export default useRequireSudo;
